import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon as IconifyIcon } from '@iconify/react';
import { formatRelativeTime } from 'src/utils/relativeTime';
import { isMultiNotification } from 'src/types/Notifications';
import {
  getNotificationNavigationUrl,
  getDeepLinkUrl,
} from 'src/utils/notificationNavigation';
import { renderIcon } from 'src/constants/icons';

const WINDOW_OPEN_FEATURES = 'noopener,noreferrer';

// Helper function to get event type icon
const getEventTypeIcon = (notificationType) => {
  const iconMap = {
    new_email: 'email',
    new_sms: 'sms',
    new_status_note: 'status',
    new_documents: 'documents',
  };
  return iconMap[notificationType] || 'notification';
};

// Helper function to get event type label
const getEventTypeLabel = (notificationType) => {
  const labelMap = {
    new_email: 'Email',
    new_sms: 'SMS',
    new_status_note: 'Status Note',
    new_documents: 'Document',
  };
  return labelMap[notificationType] || notificationType.replace('_', ' ');
};

export default function MultiNotificationDetails({
  notification,
  open,
  onClose,
}) {
  // Early return if not a multi-notification
  if (!notification || !isMultiNotification(notification)) {
    return null;
  }

  const { related_objects } = notification;
  const events = related_objects?.events || [];
  const consolidatedCount = related_objects?.consolidated_count || 0;
  const certificationId = related_objects?.certification_id;
  const propertyId = related_objects?.property_id;
  const propertyName = related_objects?.property_name;
  const notificationType = related_objects?.notification_type;
  const consolidationWindowStart = related_objects?.consolidation_window_start;
  const consolidationWindowEnd = related_objects?.consolidation_window_end;
  const isRecert =
    notification.notification_type === 'recert_notices_action_required';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh', maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconifyIcon
            icon="mdi:layers-outline"
            style={{ fontSize: '24px' }}
            color="primary"
          />
          <Typography variant="h6" component="div">
            Consolidated Notification Details
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
            {consolidatedCount}{' '}
            {isRecert
              ? 'upcoming recertification'
              : getEventTypeLabel(notificationType)}{' '}
            notification
            {consolidatedCount !== 1 ? 's' : ''} for{' '}
            {isRecert
              ? propertyName || `Property ${propertyId}`
              : notification.certifications?.[0]?.display_name ||
                notification.certification?.display_name ||
                `Certification ${certificationId}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              label={`Total Events: ${consolidatedCount}`}
              variant="outlined"
              size="small"
              color="primary"
            />
            {consolidationWindowStart && consolidationWindowEnd && (
              <Chip
                label={`Time Window: ${
                  formatRelativeTime(consolidationWindowStart).displayText
                } - ${formatRelativeTime(consolidationWindowEnd).displayText}`}
                variant="outlined"
                size="small"
              />
            )}
            <Chip
              label={`Type: ${
                isRecert
                  ? 'Recertification'
                  : getEventTypeLabel(notificationType)
              }`}
              variant="outlined"
              size="small"
              color="secondary"
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Events Table */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Event Timeline ({events.length})
          </Typography>
          {(() => {
            let instruction;
            if (isRecert) {
              instruction =
                'Click anywhere in a row to open the Recert Notices tab';
            } else if (
              notificationType === 'new_email' ||
              notificationType === 'new_sms'
            ) {
              instruction =
                'Click anywhere in a row to open the certification inbox';
            } else if (notificationType === 'new_documents') {
              instruction =
                'Click anywhere in a row to open the document manager';
            } else {
              instruction =
                'Click anywhere in a row to view the certification details';
            }
            return (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2, fontStyle: 'italic' }}
              >
                {instruction}
              </Typography>
            );
          })()}

          <TableContainer
            component={Paper}
            sx={{ maxHeight: 500, overflow: 'auto' }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      py: 1,
                      px: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      verticalAlign: 'top',
                    }}
                  >
                    {isRecert ? 'Certification' : 'Content'}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1,
                      px: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      verticalAlign: 'top',
                    }}
                  >
                    {isRecert ? 'Timeframe' : 'Time'}
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1,
                      px: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      verticalAlign: 'top',
                    }}
                  >
                    {isRecert ? 'Property' : 'Details'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length > 0 ? (
                  events.map((event) => (
                    <TableRow
                      key={event.event_id}
                      sx={{
                        cursor: 'pointer',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child': {
                          borderBottom: 'none',
                        },
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        try {
                          if (isRecert) {
                            const deepLinkRaw =
                              event.source_data?.deep_link ||
                              related_objects?.deep_link;
                            const navigationUrl = deepLinkRaw
                              ? getDeepLinkUrl({
                                  related_objects: { deep_link: deepLinkRaw },
                                })
                              : null;
                            if (navigationUrl) {
                              window.open(
                                navigationUrl,
                                '_blank',
                                WINDOW_OPEN_FEATURES
                              );
                            }
                            return;
                          }

                          const singleNotificationFromEvent = {
                            ...notification,
                            id: event.event_id,
                            notification_type: event.notification_type,
                            content: event.content,
                            created: event.created,
                            is_single_notification: true,
                            is_multi_notification: false,
                            is_bulk_notification: false,
                            related_objects: {
                              ...event.source_data,
                              type: 'single_certification',
                            },
                          };

                          // Use the same navigation utility that single notifications use
                          const navigationUrl = getNotificationNavigationUrl(
                            singleNotificationFromEvent
                          );

                          if (navigationUrl) {
                            window.open(
                              navigationUrl,
                              '_blank',
                              WINDOW_OPEN_FEATURES
                            );
                          } else {
                            console.warn(
                              'Navigation utility returned null for event:',
                              event
                            );
                            // Fallback to simple certification page
                            const fallbackUrl = `/certification/${certificationId}`;
                            window.open(
                              fallbackUrl,
                              '_blank',
                              WINDOW_OPEN_FEATURES
                            );
                          }
                        } catch (error) {
                          console.error('Error in click handler:', error);
                        }
                      }}
                    >
                      <TableCell
                        sx={{
                          py: 1,
                          px: 1,
                          fontSize: 13,
                          maxWidth: 400,
                          wordBreak: 'break-word',
                          verticalAlign: 'top',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                          }}
                        >
                          {isRecert
                            ? renderIcon('filter', 'recert', {
                                style: {
                                  fontSize: '16px',
                                  marginTop: '2px',
                                  color: '#2e7d32',
                                },
                              })
                            : renderIcon(
                                'notification',
                                getEventTypeIcon(event.notification_type),
                                {
                                  style: {
                                    fontSize: '16px',
                                    marginTop: '2px',
                                  },
                                }
                              )}
                          <Typography
                            variant="body2"
                            sx={{
                              flex: 1,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              lineHeight: 1.4,
                            }}
                          >
                            {isRecert
                              ? event.source_data?.certification_name || '-'
                              : event.content
                                  ?.replace(/ Review file\./gi, '')
                                  ?.replace(/\.\./g, '.')
                                  ?.replace(/\.\s*\./g, '.')
                                  ?.trim() || event.content}
                          </Typography>
                          {event.source_data?.attachments?.has_attachments && (
                            <Chip
                              size="small"
                              label={`${
                                event.source_data.attachments.attachment_count
                              } attachment${
                                event.source_data.attachments
                                  .attachment_count !== 1
                                  ? 's'
                                  : ''
                              }`}
                              sx={{ ml: 1, height: '16px', fontSize: '10px' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1,
                          px: 1,
                          fontSize: 13,
                          verticalAlign: 'top',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {isRecert
                            ? event.source_data?.timeframe_label || '-'
                            : formatRelativeTime(event.created).displayText}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1,
                          px: 1,
                          fontSize: 13,
                          maxWidth: 400,
                          wordBreak: 'break-word',
                          verticalAlign: 'top',
                        }}
                      >
                        {isRecert ? (
                          <Typography variant="body2" color="text.secondary">
                            {event.source_data?.property_name || '-'}
                          </Typography>
                        ) : notificationType === 'new_documents' ? (
                          <Box>
                            {event.source_data?.file_upload_question_name && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                <strong>Question:</strong>{' '}
                                {event.source_data.file_upload_question_name}
                              </Typography>
                            )}
                          </Box>
                        ) : event.source_data?.message_id ? (
                          <Typography variant="body2" color="text.secondary">
                            Message #{event.source_data.message_id}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No event details available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
